import { useId, type JSX, type ReactNode } from 'react'

import {
  StyledCard,
  StyledCardBody,
  StyledCardContent,
  StyledCardHeader,
  StyledDangerCard,
  StyledPage,
  StyledPageHeading,
  StyledRowControl,
  StyledRowDescription,
  StyledRowLabel,
  StyledRowsTable,
} from './styled'

type SettingsPageProps = {
  title: string
  description?: ReactNode
  children: ReactNode
}

export function SettingsPage({ title, description, children }: SettingsPageProps): JSX.Element {
  return (
    <StyledPage>
      <StyledPageHeading>
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </StyledPageHeading>
      {children}
    </StyledPage>
  )
}

type SettingsCardProps = {
  title: ReactNode
  extra?: ReactNode
  danger?: boolean
  children: ReactNode
}

export function SettingsCard({ title, extra, danger = false, children }: SettingsCardProps): JSX.Element {
  const titleId = useId()
  const Card = danger ? StyledDangerCard : StyledCard
  return (
    <Card aria-labelledby={titleId}>
      <StyledCardHeader>
        <h3 id={titleId}>{title}</h3>
        {extra}
      </StyledCardHeader>
      <StyledCardBody>{children}</StyledCardBody>
    </Card>
  )
}

export function SettingsRows({ children }: { children: ReactNode }): JSX.Element {
  return (
    <StyledRowsTable>
      <tbody>{children}</tbody>
    </StyledRowsTable>
  )
}

type SettingsRowProps = {
  label: ReactNode
  description?: ReactNode
  children?: ReactNode
}

export function SettingsRow({ label, description, children }: SettingsRowProps): JSX.Element {
  return (
    <tr>
      <th scope="row">
        <StyledRowLabel>{label}</StyledRowLabel>
        {description && <StyledRowDescription>{description}</StyledRowDescription>}
      </th>
      <td>
        <StyledRowControl>{children}</StyledRowControl>
      </td>
    </tr>
  )
}

export function SettingsCardContent({ children }: { children: ReactNode }): JSX.Element {
  return <StyledCardContent>{children}</StyledCardContent>
}
